import { useState, useEffect, useCallback } from 'react'
import { AlertTriangle, CheckCircle2, Clock, Eye, CheckCheck, MapPin, Calendar, Loader2, X, ZoomIn } from 'lucide-react'
import toast from 'react-hot-toast'
import type { ReportStatus, Report } from '../types'
import { fetchReports, updateReportStatus } from '../services/adminService'

const STATUS_STYLES: Record<ReportStatus, { bg: string; text: string; icon: React.ElementType; label: string }> = {
  OPEN: { bg: 'bg-red-100', text: 'text-red-700', icon: AlertTriangle, label: 'Open' },
  IN_REVIEW: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock, label: 'In Review' },
  RESOLVED: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle2, label: 'Resolved' },
  DISMISSED: { bg: 'bg-gray-100', text: 'text-gray-600', icon: Eye, label: 'Dismissed' },
}

const CATEGORY_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  ILLEGAL_DUMPING: { bg: 'bg-red-50', text: 'text-red-600', label: 'Illegal Dumping' },
  MISSED_PICKUP: { bg: 'bg-blue-50', text: 'text-blue-600', label: 'Missed Pickup' },
  COLLECTOR_COMPLAINT: { bg: 'bg-orange-50', text: 'text-orange-600', label: 'Complaint' },
  INFRASTRUCTURE: { bg: 'bg-purple-50', text: 'text-purple-600', label: 'Infrastructure' },
  OTHER: { bg: 'bg-gray-50', text: 'text-gray-600', label: 'Other' },
}

const DEFAULT_CATEGORY = { bg: 'bg-gray-50', text: 'text-gray-600', label: 'Other' }

const STATUS_FILTERS: { value: ReportStatus | 'ALL'; label: string }[] = [
  { value: 'ALL', label: 'All Reports' },
  { value: 'OPEN', label: 'Open' },
  { value: 'IN_REVIEW', label: 'In Review' },
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'DISMISSED', label: 'Dismissed' },
]

export default function Reports() {
  const [reports, setReports] = useState<Report[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState<ReportStatus | 'ALL'>('ALL')
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const PAGE_SIZE = 20

  const loadReports = useCallback(async () => {
    setLoading(true)
    try {
      // The API always paginated; this page never asked, so it showed the ten
      // newest and nothing said there were more.
      const data = await fetchReports({
        status: statusFilter !== 'ALL' ? statusFilter : undefined,
        page,
        limit: PAGE_SIZE,
      })
      setReports(data?.reports ?? data ?? [])
      setTotalPages(data?.pagination?.totalPages ?? 1)
      setTotal(data?.pagination?.total ?? (data?.reports ?? data ?? []).length)
    } catch {
      toast.error('Failed to load reports')
    } finally {
      setLoading(false)
    }
  }, [statusFilter, page])

  useEffect(() => { setPage(1) }, [statusFilter])

  useEffect(() => { loadReports() }, [loadReports])

  const handleUpdateStatus = async (reportId: string, newStatus: string) => {
    const notes = newStatus === 'DISMISSED' ? prompt('Reason for dismissal:') : undefined
    if (newStatus === 'DISMISSED' && !notes) return
    try {
      await updateReportStatus(reportId, newStatus, notes ?? undefined)
      loadReports()
    } catch {
      toast.error('Failed to update report status')
    }
  }

  const stats = {
    open: reports.filter((r) => r.status === 'OPEN').length,
    inReview: reports.filter((r) => r.status === 'IN_REVIEW').length,
    resolved: reports.filter((r) => r.status === 'RESOLVED').length,
  }

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: 'Open Reports', value: stats.open, color: 'bg-red-50 border-red-100', textColor: 'text-red-600', valColor: 'text-red-700', icon: AlertTriangle },
          { label: 'In Review', value: stats.inReview, color: 'bg-yellow-50 border-yellow-100', textColor: 'text-yellow-600', valColor: 'text-yellow-700', icon: Clock },
          { label: 'Resolved', value: stats.resolved, color: 'bg-green-50 border-green-100', textColor: 'text-green-600', valColor: 'text-green-700', icon: CheckCircle2 },
        ].map(({ label, value, color, textColor, valColor, icon: Icon }) => (
          <div key={label} className={`rounded-2xl p-5 border ${color} flex items-center gap-4`}>
            <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-sm">
              <Icon className={`w-5 h-5 ${textColor}`} />
            </div>
            <div>
              <p className={`text-xs font-medium ${textColor}`}>{label}</p>
              <p className={`text-2xl font-bold ${valColor}`}>{value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex flex-wrap gap-1.5">
            {STATUS_FILTERS.map(({ value, label }) => (
              <button
                key={value}
                onClick={() => setStatusFilter(value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap ${
                  statusFilter === value
                    ? 'bg-primary-600 text-white'
                    : 'bg-[#F8FAF9] text-[#6B7280] hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <span className="text-xs text-[#6B7280] ml-auto">{reports.length} reports</span>
        </div>
      </div>

      {/* Report Cards Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {reports.map((report) => {
            const status = STATUS_STYLES[report.status] ?? STATUS_STYLES.OPEN
            const cat = CATEGORY_STYLES[report.category] ?? DEFAULT_CATEGORY
            const StatusIcon = status.icon
            return (
              <div key={report.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
                {/* Photo or placeholder */}
                <div className="h-36 bg-gradient-to-br from-gray-100 to-gray-200 flex flex-col items-center justify-center relative">
                  {report.photoUrl ? (
                    <div className="w-full h-full relative group cursor-pointer" onClick={() => setLightboxUrl(report.photoUrl!)}>
                      <img src={report.photoUrl} alt="" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                        <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400">No photo</p>
                  )}
                  <span className={`absolute top-3 left-3 text-xs font-semibold px-2.5 py-1 rounded-full ${cat.bg} ${cat.text}`}>
                    {cat.label}
                  </span>
                  <span className="absolute top-3 right-3 text-[10px] font-mono text-gray-500 bg-white/80 px-2 py-0.5 rounded-full">
                    {report.id.slice(0, 8)}
                  </span>
                </div>

                {/* Content */}
                <div className="p-4">
                  <p className="text-sm font-bold text-[#1A1A2E] line-clamp-2 mb-2">
                    {report.title || report.description}
                  </p>

                  {report.location && (
                    <div className="flex items-center gap-1.5 text-xs text-[#6B7280] mb-1">
                      <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="truncate">{report.location}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1.5 text-xs text-[#6B7280] mb-3">
                    <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>Reported {new Date(report.createdAt).toLocaleDateString()}</span>
                    {report.user?.name && (
                      <>
                        <span className="mx-1">·</span>
                        <span>by {report.user.name}</span>
                      </>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <span className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${status.bg} ${status.text}`}>
                      <StatusIcon size={12} />
                      {status.label}
                    </span>
                    <div className="flex gap-1.5">
                      {report.status === 'OPEN' && (
                        <button
                          onClick={() => handleUpdateStatus(report.id, 'IN_REVIEW')}
                          className="flex items-center gap-1 px-2.5 py-1.5 bg-yellow-50 border border-yellow-200 rounded-lg text-xs font-semibold text-yellow-700 hover:bg-yellow-100 transition-colors"
                        >
                          <Eye size={12} />
                          Review
                        </button>
                      )}
                      {(report.status === 'OPEN' || report.status === 'IN_REVIEW') && (
                        <button
                          onClick={() => handleUpdateStatus(report.id, 'RESOLVED')}
                          className="flex items-center gap-1 px-2.5 py-1.5 bg-green-50 border border-green-200 rounded-lg text-xs font-semibold text-green-700 hover:bg-green-100 transition-colors"
                        >
                          <CheckCheck size={12} />
                          Resolve
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {!loading && reports.length === 0 && (
        <div className="bg-white rounded-2xl p-12 shadow-sm border border-gray-100 text-center">
          <AlertTriangle className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-semibold text-[#6B7280]">No reports found</p>
          <p className="text-xs text-gray-400 mt-1">Try adjusting your filters</p>
        </div>
      )}

      {/* Photo Lightbox */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setLightboxUrl(null)}
        >
          <button
            type="button"
            title="Close photo"
            className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition"
            onClick={() => setLightboxUrl(null)}
          >
            <X className="w-5 h-5 text-white" />
          </button>
          <img
            src={lightboxUrl}
            alt="Report photo"
            className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-white rounded-2xl px-4 py-3 shadow-sm border border-gray-100">
          <p className="text-xs text-[#6B7280]">Page {page} of {totalPages} · {total} reports</p>
          <div className="flex gap-2">
            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40">Previous</button>
            <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40">Next</button>
          </div>
        </div>
      )}
    </div>
  )
}
